import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { HelixLoader } from "@/components/ui/helix-loader";
import { Button } from "@/components/ui/button";
import { Flame, Trophy, PlayCircle } from "lucide-react";
import { calculateLevel } from "@/utils/levelCalculations";
import CoursePage from "./CoursePage";
import SEO from "@/components/SEO";

export default function HomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState<any>(null);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [course, setCourse] = useState<any>(null);
  const [lessonsCompletedThisWeek, setLessonsCompletedThisWeek] = useState(0);

  useEffect(() => {
    const loadHomeData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/");
        return;
      }

      const uid = session.user.id;

      const [
        { data: profileData },
        { data: recentActData }
      ] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
        supabase.from("activity_log")
          .select("*")
          .eq("user_id", uid)
          .eq("activity_type", "lesson_completed")
          .order("created_at", { ascending: false })
          .limit(3)
      ]);

      setProfile(profileData);
      setRecentActivities(recentActData || []);

      let currentCourseId = profileData?.current_course;

      if (!currentCourseId) {
        // Find latest progress
        const { data: progressData } = await supabase
          .from("user_progress")
          .select("lesson_id")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(1);

        if (progressData && progressData.length > 0) {
          const { data: lessonData } = await supabase
            .from("lessons")
            .select("course_id")
            .eq("id", progressData[0].lesson_id)
            .maybeSingle();

          if (lessonData?.course_id) {
            currentCourseId = lessonData.course_id;
            await supabase.from("profiles").update({ current_course: currentCourseId }).eq("id", uid);
          }
        }
      }

      if (currentCourseId) {
        const { data: courseData } = await supabase.from("courses").select("title, slug").eq("id", currentCourseId).maybeSingle();
        setCourse(courseData);
      }

      // Calculate lessons completed this week
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const { count } = await supabase.from("activity_log")
        .select("*", { count: "exact", head: true })
        .eq("user_id", uid)
        .eq("activity_type", "lesson_completed")
        .gte("created_at", oneWeekAgo.toISOString());

      setLessonsCompletedThisWeek(count || 0);

      setLoading(false);
    };

    loadHomeData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <HelixLoader className="text-primary" />
      </div>
    );
  }

  if (course) {
    return <CoursePage courseSlugProp={course.slug} />;
  }

    const level = calculateLevel(profile?.xp_points || 0);

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl flex flex-col gap-8 pb-24">
      <SEO 
        title="Learn Astrophysics & Physics" 
        description="Scico is a gamified science learning platform for students in grades 6-12 and beyond." 
      />
      {/* TOP SECTION - Today's Goal */}
      <section className="bg-card border border-border/50 rounded-2xl p-6 shadow-sm flex flex-col gap-4">
        <h1 className="text-2xl font-bold">Ready to learn?</h1>

        <div className="flex justify-between items-center bg-background/50 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Flame className="text-orange-500 w-8 h-8" />
            <div>
              <p className="font-bold text-lg">{profile?.streak_count || 0} Day Streak</p>
              <p className="text-sm text-muted-foreground">Choose a subject to start today's goal</p>
            </div>
          </div>
        </div>

        <Button
          size="lg"
          className="w-full text-lg h-14 font-semibold"
          onClick={() => navigate(course ? `/learning/${course.slug}` : "/learning")}
        >
          {course ? `Continue ${course.title}` : "Choose a Subject"}
        </Button>
      </section>

      {/* MIDDLE SECTION - Stats Row */}
      <section className="grid grid-cols-3 gap-4">
        <div className="bg-card border border-border/50 rounded-xl p-4 flex flex-col items-center justify-center text-center gap-2">
          <Trophy className="text-yellow-500 w-6 h-6" />
          <p className="font-bold text-xl">{profile?.xp_points || 0}</p>
          <p className="text-xs text-muted-foreground">{`Level ${level}`}</p>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-4 flex flex-col items-center justify-center text-center gap-2">
          <Flame className="text-orange-500 w-6 h-6" />
          <p className="font-bold text-xl">{profile?.streak_count || 0}</p>
          <p className="text-xs text-muted-foreground">Day Streak</p>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-4 flex flex-col items-center justify-center text-center gap-2">
          <PlayCircle className="text-blue-500 w-6 h-6" />
          <p className="font-bold text-xl">{lessonsCompletedThisWeek}</p>
          <p className="text-xs text-muted-foreground">Lessons this week</p>
        </div>
      </section>

      {/* BOTTOM SECTION - Recent Activity */}
      <section className="bg-card border border-border/50 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-bold mb-4">Recent Activity</h3>

        {recentActivities.length > 0 ? (
          <div className="flex flex-col gap-3">
            {recentActivities.map((act) => (
              <div key={act.id} className="flex items-center gap-4 bg-background/50 rounded-xl p-3 border border-border/30">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <PlayCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">
                    {act.metadata?.lesson_title || 'Unknown Lesson'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(act.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-sm font-bold text-green-500 bg-green-500/10 px-2 py-1 rounded-md">
                  +{act.xp_earned} XP
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-background/50 rounded-xl border border-border/30">
            <p className="text-muted-foreground">No lessons completed recently.</p>
            <p className="text-sm font-medium mt-1">Keep going!</p>
          </div>
        )}
      </section>
    </div>
  );
}
